import type { Monaco } from '@monaco-editor/react';

import type { EnvironmentVariable } from '../types';
// @ts-expect-error - uses webpack to import URL to fetch raw text file
import customFunctionTypesUrl from './dts-files/lib.custom-function.d.ts.txt';
// @ts-expect-error - uses webpack to import URL to fetch raw text file
import denoTypesUrl from './dts-files/lib.deno.d.ts.txt';

const logger = console;

/**
 * Add global types to the monaco editor
 *
 * @param params - see inline docs
 */
function addGlobalTypes({
  monaco,
  dTsRawText,
  filename,
}: {
  /** The monaco editor instance */
  monaco: Monaco;
  /** The raw text of the TypeScript definition file (.d.ts) */
  dTsRawText: string;
  /** The filename for the TypeScript definition file, used for cosmetic purposes (e.g., `lib.deno.d.ts`) */
  filename: string;
}): void {
  try {
    // Add Deno's types as an extra library
    monaco.languages.typescript.typescriptDefaults.addExtraLib(
      dTsRawText,
      `file:///${filename}`,
    );
  } catch (error) {
    logger.error(`Failed to add global types for ${filename}`, error);
  }
}

/**
 * Configure the editor for Deno
 *
 * @param monaco - monaco editor instance
 * @returns Promise<void>
 */
async function configureDeno(monaco: Monaco): Promise<void> {
  /**
   * Configure TypeScript compiler options to match Deno’s environment
   *
   * @see https://docs.deno.com/runtime/reference/ts_config_migration/#ts-compiler-options
   */
  monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
    target: monaco.languages.typescript.ScriptTarget.ESNext,
    module: monaco.languages.typescript.ModuleKind.ESNext,
    allowJs: true,
    allowUnreachableCode: false,
    allowUnusedLabels: false,
    checkJs: false,
    // jsx: 'react',
    // jsxFactory: 'React.createElement',
    // jsxFragmentFactory: 'React.Fragment',
    keyofStringsOnly: false,
    noErrorTruncation: false,
    noFallthroughCasesInSwitch: false,
    noImplicitAny: true,
    noImplicitOverride: true,
    noImplicitReturns: false,
    noImplicitThis: true,
    noImplicitUseStrict: true,
    noStrictGenericChecks: false,
    noUnusedLocals: false,
    noUnusedParameters: false,
    noUncheckedIndexedAccess: false,
    // reactNamespace: 'React',
    strict: true,
    strictBindCallApply: true,
    strictFunctionTypes: true,
    strictPropertyInitialization: true,
    strictNullChecks: true,
    suppressExcessPropertyErrors: false,
    suppressImplicitAnyIndexErrors: false,
    useUnknownInCatchVariables: true,
  });

  /**
   * Add Deno’s global type definitions
   *
   * @see https://docs.deno.com/runtime/reference/cli/types/
   */
  const rawTypes = await fetch(denoTypesUrl).then((response) =>
    response.text(),
  );
  return addGlobalTypes({
    monaco,
    dTsRawText: rawTypes,
    filename: 'lib.deno.d.ts',
  });
}

/**
 * Add CustomFunction global type definitions
 *
 * @param monaco - monaco editor instance
 * @param envVars - environment variables set in Environment Variables tab
 * @returns Promise<void>
 */
async function addCustomFunctionGlobalTypes(
  monaco: Monaco,
  envVars: EnvironmentVariable[],
): Promise<void> {
  const rawTypes = await fetch(customFunctionTypesUrl).then((response) =>
    response.text(),
  );

  // Add types for environment variables from the Environment Variables tab
  addGlobalTypes({
    monaco,
    dTsRawText: `/** The global namespace for Transcend Custom Functions. */
declare namespace EnvironmentGlobals {
  interface Environment {
${envVars
  .map((envVar) => {
    const { name } = envVar;
    return `    /** An environment variable set by you on the Environment Variables tab of your Custom Function. */
    ${name}: string;`;
  })
  .join('\n')}
  }
}`,
    filename: 'lib.custom-function-environment.d.ts',
  });

  addGlobalTypes({
    monaco,
    dTsRawText: rawTypes,
    filename: 'lib.custom-function.d.ts',
  });
}

/**
 * Configure the editor for Deno
 *
 * @param monaco - monaco editor instance
 * @param envVars - environment variables set in Environment Variables tab
 */
export async function enableCustomFunctionDenoMode(
  monaco: Monaco,
  envVars: EnvironmentVariable[],
): Promise<void> {
  await Promise.all([
    addCustomFunctionGlobalTypes(monaco, envVars),
    configureDeno(monaco),
  ]);
}
